/**
 * SOLOMON — TOOL MODAL
 * Shared overlay wrapper for tool panels (Signals, write tools) and
 * write tools (Calendar, Tasks, Sheets, Leads). Backdrop click + ESC
 * close, glass-styled content panel with title row.
 *
 * Backdrop close uses mousedown-AND-click origin tracking instead of a bare
 * onClick on the overlay: a synthetic click whose mousedown originated on
 * the content (e.g. the user dragging a text selection out of the modal)
 * was previously closing the modal mid-selection. We now require both
 * mousedown and the resulting click to land on the overlay element itself.
 */

import { useEffect, useId, useRef } from 'react';
import { pushLayer, popLayer, isTopLayer } from '../../layers';

export default function ToolModal({ title, onClose, children }) {
  const overlayRef = useRef(null);
  const mouseDownTargetRef = useRef(null);
  const layerId = useId();

  // ESC closes the modal — unified UX across all tools — but only while this
  // modal is the topmost layer. The modal is mounted only while open, so the
  // effect's lifetime IS its open condition.
  useEffect(() => {
    pushLayer(layerId);
    const onKey = (e) => { if (e.key === 'Escape' && isTopLayer(layerId)) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { popLayer(layerId); window.removeEventListener('keydown', onKey); };
  }, [onClose, layerId]);

  const handleMouseDown = (e) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleClick = (e) => {
    // Only close when the press AND the click both land on the overlay.
    // This rejects text-selection drags that originate inside content and
    // release on the backdrop (browser fires click on the common ancestor).
    if (e.target === overlayRef.current && mouseDownTargetRef.current === overlayRef.current) {
      onClose();
    }
  };

  return (
    <div
      ref={overlayRef}
      className="visual-tool-modal-overlay"
      onMouseDown={handleMouseDown}
      onClick={handleClick}
    >
      <div className="visual-tool-modal-content">
        <div className="visual-tool-modal-header">
          <span className="visual-tool-modal-title">{title}</span>
          <button
            className="visual-tool-modal-close"
            onClick={onClose}
            aria-label={`Close ${title}`}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
